"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Globe, Database, PhoneCall, MessageSquare, CalendarCheck, Mail, MessageCircle, Workflow, ChartBar as BarChart3, Star, ArrowRight, Zap, Clock, CircleCheck as CheckCircle2, Send, Inbox } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
const nodesData = [{
  id: "website",
  name: "Website",
  iconName: "Globe",
  role: "Captures online visitors and turns anonymous traffic into qualified leads.",
  sends: "Form submissions, page engagement events, and instant chat triggers.",
  receives: "Real-time calendar availability, service ratings, and custom landing page updates."
}, {
  id: "crm",
  name: "CRM",
  iconName: "Database",
  role: "Central source of truth storing contact profiles, lead statuses, and deal records.",
  sends: "Customer records, stage changes, and deal triggers to automated workflows.",
  receives: "New contact details, call transcripts, appointment confirmations, and text replies."
}, {
  id: "receptionist",
  name: "AI Receptionist",
  iconName: "PhoneCall",
  role: "Answers incoming phone calls 24/7, answers questions, and qualifies prospects.",
  sends: "Call transcripts, caller contact details, and appointment requests.",
  receives: "Updated business FAQs, service price sheets, and team schedule calendars."
}, {
  id: "chat",
  name: "AI Chat",
  iconName: "MessageSquare",
  role: "Engages web visitors instantly to answer inquiries and capture lead details.",
  sends: "Chat transcripts, lead intent tags, and preferred booking time slots.",
  receives: "Knowledge base documentation and real-time appointment availability."
}, {
  id: "calendars",
  name: "Calendars",
  iconName: "CalendarCheck",
  role: "Syncs team availability and schedules client appointments without conflicts.",
  sends: "Confirmed booking times, cancellation notices, and reschedule alerts.",
  receives: "New booking requests from AI receptionists, web chat, and contact forms."
}, {
  id: "email",
  name: "Email",
  iconName: "Mail",
  role: "Delivers booking confirmations, client nurture campaigns, and staff notifications.",
  sends: "Email open events, link clicks, and subscriber response signals.",
  receives: "Automated trigger instructions and personalized contact field data."
}, {
  id: "sms",
  name: "SMS",
  iconName: "MessageCircle",
  role: "Sends instant text confirmations, reminders, and handles two-way messaging.",
  sends: "Inbound customer replies, delivery receipts, and opt-out preferences.",
  receives: "Automated message templates, reminder prompts, and review links."
}, {
  id: "automations",
  name: "Automations",
  iconName: "Workflow",
  role: "Executes backend workflow rules that link incoming triggers to system actions.",
  sends: "Execution commands to SMS, Email, CRM, and appointment calendars.",
  receives: "Pipeline updates, time-based delays, and customer status changes."
}, {
  id: "analytics",
  name: "Analytics",
  iconName: "BarChart3",
  role: "Tracks lead response times, conversion rates, and overall revenue attribution.",
  sends: "Performance summaries, ROI metrics, and volume alerts.",
  receives: "Call outcomes, form completions, and closed deal data from CRM."
}, {
  id: "reviews",
  name: "Review Management",
  iconName: "Star",
  role: "Requests reviews from satisfied customers automatically following service.",
  sends: "New customer ratings, feedback alerts, and public review links.",
  receives: "Completed service triggers and updated customer contact records."
}];
const lifecycleSteps = [{
  step: 1,
  title: "Initial Contact",
  description: "Visitor asks a question on your site or calls after hours."
}, {
  step: 2,
  title: "AI Response",
  description: "AI receptionist or chat answers instantly and captures contact details."
}, {
  step: 3,
  title: "CRM Sync",
  description: "Contact and notes are created in CRM instantly without manual typing."
}, {
  step: 4,
  title: "Booking & Alerts",
  description: "Appointment is booked on calendar with SMS and email confirmation."
}, {
  step: 5,
  title: "Follow-Up",
  description: "Automated follow-up sequence and review request fire automatically."
}];
function renderNodeIcon(iconName: string, className?: string) {
  switch (iconName) {
    case "Globe":
      return <Globe className={className} strokeWidth={1.25} />;
    case "Database":
      return <Database className={className} strokeWidth={1.25} />;
    case "PhoneCall":
      return <PhoneCall className={className} strokeWidth={1.25} />;
    case "MessageSquare":
      return <MessageSquare className={className} strokeWidth={1.25} />;
    case "CalendarCheck":
      return <CalendarCheck className={className} strokeWidth={1.25} />;
    case "Mail":
      return <Mail className={className} strokeWidth={1.25} />;
    case "MessageCircle":
      return <MessageCircle className={className} strokeWidth={1.25} />;
    case "Workflow":
      return <Workflow className={className} strokeWidth={1.25} />;
    case "BarChart3":
      return <BarChart3 className={className} strokeWidth={1.25} />;
    case "Star":
      return <Star className={className} strokeWidth={1.25} />;
    default:
      return <Globe className={className} strokeWidth={1.25} />;
  }
}
export default function Interactiveintegrationdashboard() {
  const [selectedNodeId, setSelectedNodeId] = useState<string>("website");
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(true);
  const shouldReduceMotion = useReducedMotion();
  const selectedNode = nodesData.find(node => node.id === selectedNodeId) || nodesData[0];
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % lifecycleSteps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);
  const progressValue = (activeStep + 1) / lifecycleSteps.length * 100;
  return <section id="interactiveintegrationdashboard" data-nav="light" className="bg-background text-foreground py-24 md:py-32 border-y border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="font-mono text-xs uppercase tracking-widest px-3 py-1 mb-4 border-border text-muted-foreground">
            HOW IT WORKS
          </Badge>
          <h2 className="text-3xl md:text-5xl font-light tracking-tight mb-4">
            One Connected Ecosystem, Working{" "}
            <span className="font-serif italic text-[var(--accent-deep-red)] font-normal">Automatically</span>.
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Information flows between platforms without re-typing, manual exports, or delayed responses.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-2 xl:grid-cols-5 gap-3">
              {nodesData.map((node, i) => {
              const isSelected = node.id === selectedNodeId;
              return <div key={node.id} data-index={i} className="w-full">
                    <Button variant="outline" onClick={() => setSelectedNodeId(node.id)} className={`w-full h-28 flex flex-col items-center justify-center gap-2.5 p-3 rounded-xl border transition-all duration-200 cursor-pointer ${isSelected ? "bg-card border-[var(--accent-deep-red)] text-foreground shadow-lg ring-1 ring-[var(--accent-deep-red)]/30 scale-[1.02]" : "bg-card/50 border-border/60 text-muted-foreground hover:border-border hover:text-foreground hover:bg-card"}`}>
                      <div className={`p-2 rounded-lg transition-colors ${isSelected ? "bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)]" : "text-muted-foreground"}`}>
                        {renderNodeIcon(node.iconName, "size-6")}
                      </div>
                      <span className="text-xs font-medium truncate w-full text-center">
                        {node.name}
                      </span>
                    </Button>
                  </div>;
            })}
            </div>

            <div className="mt-4 p-4 rounded-xl border border-border/60 bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-[var(--accent-deep-red)] animate-pulse" />
                Select any system node above to inspect detail flows
              </span>
              <span className="font-mono hidden sm:inline">10 Active Connections</span>
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-8">
            <Card className="bg-card border-border shadow-md rounded-2xl overflow-hidden">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] border border-[var(--accent-deep-red)]/20">
                      {renderNodeIcon(selectedNode.iconName, "size-6")}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-foreground">
                        {selectedNode.name}
                      </CardTitle>
                      <Badge variant="secondary" className="mt-1 text-[10px] font-mono uppercase tracking-wider">
                        Active Node
                      </Badge>
                    </div>
                  </div>
                </div>
                <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-1">
                  {selectedNode.role}
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6 space-y-6">
                <AnimatePresence mode="wait">
                  <motion.div key={selectedNode.id} initial={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="space-y-6">
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 text-xs font-mono font-medium text-foreground uppercase tracking-wider">
                        <Send className="size-3.5 text-[var(--accent-deep-red)]" />
                        <span>What It Sends Out</span>
                      </div>
                      <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-sm text-foreground/90 leading-relaxed">
                        {selectedNode.sends}
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 text-xs font-mono font-medium text-foreground uppercase tracking-wider">
                        <Inbox className="size-3.5 text-[var(--accent-deep-red)]" />
                        <span>What It Receives</span>
                      </div>
                      <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-sm text-foreground/90 leading-relaxed">
                        {selectedNode.receives}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="pt-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-card border border-border text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5 font-mono">
                      <Zap className="size-3.5 text-[var(--accent-deep-red)]" /> Instant Sync
                    </span>
                    <span className="font-mono text-foreground font-medium">0ms Data Delay</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="border border-border rounded-2xl bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-6 border-b border-border/60">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
                <Workflow className="size-5 text-[var(--accent-deep-red)]" />
                Automated Lead Lifecycle Sequence
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Watch how a single client inquiry triggers consecutive tasks automatically.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="font-mono text-xs py-1 px-3 border-border">
                <Clock className="size-3 mr-1 text-[var(--accent-deep-red)]" />
                Avg handoff: &lt; 2 seconds
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => setIsAutoPlaying(!isAutoPlaying)} className="text-xs font-mono border border-border">
                {isAutoPlaying ? "Pause Loop" : "Play Loop"}
              </Button>
            </div>
          </div>

          <div className="mb-6">
            <Progress value={progressValue} className="h-1.5 bg-muted" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {lifecycleSteps.map((item, i) => {
            const isActive = activeStep === i;
            return <div key={item.step} data-index={i} className="w-full">
                  <Button variant="outline" onClick={() => {
                setActiveStep(i);
                setIsAutoPlaying(false);
              }} className={`w-full h-auto text-left flex flex-col items-start justify-start p-4 rounded-xl border transition-all duration-200 cursor-pointer ${isActive ? "bg-card border-[var(--accent-deep-red)] shadow-md ring-1 ring-[var(--accent-deep-red)]/20" : "bg-muted/20 border-border/50 opacity-70 hover:opacity-100 hover:bg-card"}`}>
                    <div className="flex items-center justify-between w-full mb-2">
                      <span className={`size-6 rounded-full text-xs font-mono font-bold flex items-center justify-center ${isActive ? "bg-[var(--accent-deep-red)] text-white" : "bg-muted text-muted-foreground"}`}>
                        {item.step}
                      </span>
                      {isActive && <CheckCircle2 className="size-4 text-[var(--accent-deep-red)]" />}
                    </div>
                    <span className="text-xs font-semibold text-foreground mb-1 block">
                      {item.title}
                    </span>
                    <p className="text-[11px] text-muted-foreground leading-snug font-normal whitespace-normal">
                      {item.description}
                    </p>
                  </Button>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}